import { AddressAutocomplete } from "@components/address-autocomplete/address-autocomplete";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { BaseProjectProps } from "@custom-types/project-types";
import { EditIcon, neutral, Text } from "@flat-ui";
import { Box, CircularProgress, ClickAwayListener, Stack } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { GeocodedAddress } from "@utils/geocoding-utils";
import { parseCoordinatesString } from "@utils/string-utils";
import { ChangeProjectDetailsEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { DASH } from "@utils/ui-utils";
import { useState } from "react";

/** Payload to update the project address and GPS position fields */
interface UpdateAddressPayload {
  /** The new address to update */
  address: string;

  /**
   * The new position to update, if available.
   * If the value is null then the position will be unset in the backend.
   */
  position?: FaroCoreApiTypes.Position | null;
}

interface Props extends BaseProjectProps {
  /** Whether the project is editable or not */
  isProjectEditable: boolean;
}

/**
 * @returns A component that displays the project address.
 * If the project is editable, it allows the user to edit the address.
 * Saves automatically when the user selects a geocoded suggestion, presses Enter, or clicks away.
 * Cancels without saving when the user presses Escape or exits without making changes.
 */
export function ProjectAddress({
  project,
  isProjectEditable,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [input, setInput] = useState<string>(project.address);

  /** Enter edit mode */
  function enterEditMode(): void {
    setIsEditMode(true);
    setIsUpdating(false);
    setInput(project.address);
  }

  /** Exits the edit mode */
  function exitEditMode(): void {
    setIsEditMode(false);
  }

  /** Updates the address (and optionally position) to the backend, then exits edit mode */
  async function updateAddress(payload: UpdateAddressPayload): Promise<void> {
    trackEvent({ name: ChangeProjectDetailsEvents.changeProjectAddress });
    setIsUpdating(true);
    await dispatch(
      updateProjectDetails({
        projectId: project.id,
        payload,
      })
    );
    setIsUpdating(false);
    exitEditMode();
  }

  /**
   * Handles the selection of a geocoded address.
   * Sets the input value to the geocoded address and updates the backend with the new address and position.
   * @param geocodedAddress The selected geocoded address
   */
  function onGeocodedAddressSelect(geocodedAddress: GeocodedAddress): void {
    setInput(geocodedAddress.address);
    void updateAddress({
      address: geocodedAddress.address,
      position: geocodedAddress.position,
    });
  }

  /**
   * Handles saving a custom address typed by the user.
   */
  function onCustomAddressSelect(value: string): void {
    let address = value.trim();
    let position = undefined;

    // If the address has not changed then exit edit mode without any update
    if (address === project.address) {
      exitEditMode();
      return;
    }

    // If the user cleared the address value then also unset the position.
    // A null value for position will unset it in the backend.
    // Also assign a dash char to the address as an accepted convention to update
    // the backend with an empty address.
    if (address.length === 0) {
      address = DASH;
      position = null;
    }

    // If the custom address value is a valid coordinates string, then parse it
    // and assign the parsed coordinates to the position field.
    const parsedCoordinates = parseCoordinatesString(address);
    if (parsedCoordinates) {
      position = parsedCoordinates;
    }

    void updateAddress({ address, position });
  }

  return (
    <Box sx={{ mb: "30px" }}>
      <Text variant="bodyS" sx={{ color: neutral[500] }}>
        Address
      </Text>
      <ClickAwayListener
        onClickAway={() => {
          if (isEditMode) {
            onCustomAddressSelect(input);
          }
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => {
            if (isProjectEditable && !isEditMode) {
              enterEditMode();
            }
          }}
          sx={{
            cursor: isProjectEditable ? "pointer" : "default",
            justifyContent: "space-between",
            minHeight: "39px",
          }}
        >
          {isEditMode ? (
            <AddressAutocomplete
              input={input}
              setInput={setInput}
              onGeocodedAddressSelect={onGeocodedAddressSelect}
              onCustomAddressSelect={onCustomAddressSelect}
              onLoadingGeocodedAddressChange={(state) => {
                // Exit edit mode if the request to geocode the address suggestions fails.
                // The AddressAutocomplete component already takes care of showing an error toast.
                if (state === "rejected") {
                  exitEditMode();
                }
              }}
              onCancel={exitEditMode}
              variant="standard"
              isDisabled={isUpdating}
            />
          ) : (
            <Text variant="bodyM">
              <var>{project.address || DASH}</var>
            </Text>
          )}
          {isProjectEditable && isHovered && !isEditMode && (
            <SphereIconButton
              component={EditIcon}
              buttonSize="24px"
              iconSize="16px"
              onClick={enterEditMode}
              color={neutral[400]}
            />
          )}
          {isUpdating && <CircularProgress size={16} />}
        </Stack>
      </ClickAwayListener>
    </Box>
  );
}
